<template>
  <div class="ring-background-fg/5 overflow-hidden rounded-lg shadow ring-1">
    <div class="relative overflow-x-auto">
      <template v-if="isLoading">
        <div class="absolute inset-x-0 top-0">
          <BaseLoadingBar />
        </div>
      </template>
      <table
        class="divide-background-fg/5 min-w-full divide-y-2 overflow-x-auto text-xs tabular-nums"
      >
        <thead class="bg-background-fg/3">
          <tr>
            <template v-for="column in columns" :key="column.name">
              <th
                scope="col"
                class="py-6 font-semibold text-black/90"
                :class="[
                  hiddenClass(column),
                  wrapHeaders
                    ? 'whitespace-normal px-3 align-bottom'
                    : 'whitespace-nowrap px-4',
                  column.align === 'right'
                    ? 'text-right'
                    : column.align === 'center'
                      ? 'text-center'
                      : 'text-left',
                  column.isSortable ? 'cursor-pointer' : '',
                ]"
                @click="onClickHeader(column)"
              >
                <div
                  class="flex space-x-2"
                  :class="
                    column.align === 'right'
                      ? 'justify-end'
                      : column.align === 'center'
                        ? 'justify-center'
                        : 'justify-start'
                  "
                >
                  <span>
                    <slot :name="`header:${column.name}`" v-bind="{ column }">
                      <BaseTooltip v-if="column.tooltip" placement="bottom">
                        <template #trigger>
                          {{ column.label }}
                        </template>
                        {{ column.tooltip }}
                      </BaseTooltip>
                      <span v-else>{{ column.label }}</span>
                    </slot>
                  </span>
                  <template v-if="column.isSortable">
                    <template v-if="sortBy === column.name">
                      <IconHeroiconsSolidChevronUp class="h-4 w-4" />
                    </template>
                    <template v-else-if="sortBy === `-${column.name}`">
                      <IconHeroiconsSolidChevronDown class="h-4 w-4" />
                    </template>
                    <template v-else>
                      <IconHeroiconsSolidChevronUpDown class="h-4 w-4" />
                    </template>
                  </template>
                </div>
              </th>
            </template>
          </tr>
        </thead>

        <tbody class="divide-background-fg/5 divide-y-2 bg-white">
          <template v-if="rows && rows.length > 0">
            <template v-for="(row, rowIndex) in rows" :key="row[keyField]">
              <tr
                :class="[
                  typeof rowClass === 'function' ? rowClass(row) : rowClass,
                  draggingIndex === rowIndex ? 'opacity-40' : '',
                  dropIndex === rowIndex ? 'border-t-primary border-t-2' : '',
                ]"
                :draggable="isReorderable"
                @click="emit('clickRow', row)"
                @dragstart="onDragStart(rowIndex)"
                @dragover.prevent="onDragOver(rowIndex)"
                @drop.prevent="onDrop"
                @dragend="onDragEnd"
              >
                <template v-for="column in columns" :key="column.name">
                  <td
                    :class="[
                      column.autoWidth ? 'w-0' : '',
                      hiddenClass(column),
                      'whitespace-nowrap px-3 py-2 text-black/70',
                      column.align === 'right'
                        ? 'text-right'
                        : column.align === 'center'
                          ? 'text-center'
                          : 'text-left',
                    ]"
                  >
                    <slot
                      :name="`body-cell:${column.name}`"
                      v-bind="{
                        value: column.getValue(row),
                      }"
                    >
                      <span
                        v-if="column.renderHtml"
                        v-html="column.getValue(row)"
                      ></span>
                      <span v-else>{{ column.getValue(row) }}</span>
                    </slot>
                  </td>
                </template>
              </tr>
            </template>
          </template>
          <template v-else-if="rows">
            <tr>
              <td :colspan="columns.length">
                <span
                  class="text-background-fg/70 block py-2 text-center text-base font-medium"
                >
                  No records found
                </span>
              </td>
            </tr>
          </template>
        </tbody>
        <tfoot
          v-if="footers && footers.length > 0"
          class="divide-background-fg/5 bg-background-fg/3 divide-y-2"
        >
          <template v-for="(row, index) in footers" :key="index">
            <tr>
              <template v-for="column in columns" :key="column.name">
                <td
                  :class="[
                    column.autoWidth ? 'w-0' : '',
                    hiddenClass(column),
                    'whitespace-nowrap px-3 py-2 font-semibold text-black/90',
                    column.align === 'right'
                      ? 'text-right'
                      : column.align === 'center'
                        ? 'text-center'
                        : 'text-left',
                  ]"
                >
                  <slot
                    :name="`footer-cell:${column.name}`"
                    v-bind="{
                      value: column.getValue(row),
                    }"
                  >
                    <span
                      v-if="column.renderHtml"
                      v-html="column.getValue(row)"
                    ></span>
                    <span v-else>{{ column.getValue(row) }}</span>
                  </slot>
                </td>
              </template>
            </tr>
          </template>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts" generic="TRow">
import { type ColumnDefinition } from "@/types/table";

type Props<T> = {
  columns: Array<ColumnDefinition<T>>;
  rows: Array<T>;
  /** Classes for every row, or per row (e.g. a red background for a flagged row). */
  rowClass?: string | ((row: T) => string);
  isLoading: boolean;
  sortBy?: string | null;
  keyField: keyof T;
  footers?: Array<T>;
  /** Allow header labels to wrap onto multiple lines (avoids wide tables / horizontal scroll). */
  wrapHeaders?: boolean;
  /**
   * Let rows be dragged into a new order, emitting `reorder` with the moved
   * row's old and new index. The whole row is the drag target — give it a grip
   * icon so the affordance is visible.
   */
  isReorderable?: boolean;
};

const props = defineProps<Props<TRow>>();

const emit = defineEmits<{
  (event: "clickRow", params: TRow): void;
  (event: "update:sortBy", value: string | null): void;
  (event: "reorder", from: number, to: number): void;
}>();

const HIDDEN_BELOW = {
  sm: "hidden sm:table-cell",
  md: "hidden md:table-cell",
  lg: "hidden lg:table-cell",
  xl: "hidden xl:table-cell",
} as const;

const hiddenClass = (column: ColumnDefinition<TRow>) =>
  column.hideBelow ? HIDDEN_BELOW[column.hideBelow] : "";

// Native HTML5 drag: no dependency, and it is the browser's own affordance.
// It doesn't fire on touch, so reordering is a pointer-device job.
const draggingIndex = ref<number | null>(null);
const dropIndex = ref<number | null>(null);

const onDragStart = (index: number) => {
  draggingIndex.value = index;
};

const onDragOver = (index: number) => {
  if (draggingIndex.value !== null) dropIndex.value = index;
};

const onDrop = () => {
  const from = draggingIndex.value;
  const to = dropIndex.value;
  if (from !== null && to !== null && from !== to) emit("reorder", from, to);
  onDragEnd();
};

const onDragEnd = () => {
  draggingIndex.value = null;
  dropIndex.value = null;
};

defineSlots<
  {
    [K in `header:${string}`]: (props: {
      column: ColumnDefinition<TRow>;
    }) => any;
  } & {
    [K in `body-cell:${string}`]: (props: { value: any }) => any;
  } & {
    [K in `footer-cell:${string}`]: (props: { value: any }) => any;
  }
>();

const onClickHeader = (column: ColumnDefinition<TRow>) => {
  if (!column.isSortable) {
    return;
  } else if (props.sortBy === `-${column.name}`) {
    emit("update:sortBy", column.name);
  } else if (props.sortBy === column.name) {
    emit("update:sortBy", null);
  } else {
    emit("update:sortBy", `-${column.name}`);
  }
};
</script>
